import ButtonLoader from 'components/ButtonLoader'
import ContentBoxBackgroundContent from 'components/ContentBoxBackgroundContent'
import Form from 'components/Form'
import FormButtons from 'components/Form/Form.Buttons'
import FormGroup from 'components/Form/Form.Group'
import Formik from 'components/Form/Formik'
import Input from 'components/Input'
import Paragraph from 'components/Paragraph'
import TextBodySmall from 'components/TextBodySmall'
import TextTitleCondensed from 'components/TextTitleCondensed'
import { View } from 'components/View'
import formatMessageIntlUtil from 'modules/intl/utils/format-message'
import React from 'react'
import { defineMessages } from 'react-intl'
import useIntl from 'modules/component/utils/hooks/useIntl'
import * as Yup from 'yup'
import { logEventAnalyticsUtil } from 'modules/analytics/utils/log-event/index.js'

import NewsletterFormSuccess from 'components/Newsletter/components/FormSuccess/index.js'
import NewsletterFormFailure from 'components/Newsletter/components/FormFailure/index.js'
import {
  NEWSLETTER_KEY,
  NEWSLETTER_SUBMIT_STATUS
} from 'components/Newsletter/constants/index.js'
import handleFormSubmitNewsletterUtil from 'components/Newsletter/utils/handle-form-submit/index.js'

const messages = defineMessages({
  firstName: {
    id: 'Newsletter.firstName',
    defaultMessage: 'First name'
  },
  newsletter: {
    id: 'Newsletter.newsletter',
    defaultMessage: 'Newsletter'
  },
  join: {
    id: 'Newsletter.join',
    defaultMessage: 'Join'
  },
  email: {
    id: 'Newsletter.email',
    defaultMessage: 'Email'
  },
  description: {
    id: 'Newsletter.description',
    defaultMessage: 'Weekly knowledge exclusively for people who want to improve their health, fitness and mindset.'
  }
})

const validationSchema = Yup.object()
  .shape({
    [NEWSLETTER_KEY.FIRST_NAME]: Yup.string()
      .required('Please enter your first name.'),
    [NEWSLETTER_KEY.EMAIL]: Yup.string()
      .email('Please enter a valid email.')
      .required('Please enter your email.'),
    [NEWSLETTER_KEY.BOT_CHECKER]: Yup.string()
  })

const EVENT_LABEL = 'JOIN_NEWSLETTER'

/**
 * @param props
 * @param props.description
 * @return {Element}
 * @constructor
 */
export default function Newsletter (props) {
  const {
    description,
    ...rest
  } = props
  const intl = useIntl()
  const [submitStatus, setSubmitStatus] = React.useState()

  const handleClick = () => {
    logEventAnalyticsUtil(EVENT_LABEL)
  }

  const renderFormContent = ({
    errors,
    touched,
    isSubmitting,
    handleChange,
    handleBlur,
    handleSubmit,
    values,
    isValid,
    dirty
  }) => {
    return (
      <Form
        onSubmit={handleSubmit}
        onKeySubmit={false}
      >
        <TextTitleCondensed>
          {formatMessageIntlUtil(intl, messages.newsletter)}
        </TextTitleCondensed>

        <Paragraph>
          <TextBodySmall>
            {description || formatMessageIntlUtil(intl, messages.description)}
          </TextBodySmall>
        </Paragraph>

        <FormGroup>
          <Input
            placeholder={formatMessageIntlUtil(intl, messages.firstName)}
            type='text'
            name={NEWSLETTER_KEY.FIRST_NAME}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[NEWSLETTER_KEY.FIRST_NAME]}
            colorType={
              errors[NEWSLETTER_KEY.FIRST_NAME] &&
              touched[NEWSLETTER_KEY.FIRST_NAME]
                ? Input.COLOR_TYPE.ERROR
                : null
            }
          />
        </FormGroup>

        <FormGroup>
          <Input
            placeholder={
              formatMessageIntlUtil(intl, messages.email)
            }
            type='email'
            name={NEWSLETTER_KEY.EMAIL}
            onChange={handleChange}
            onBlur={handleBlur}
            value={values[NEWSLETTER_KEY.EMAIL]}
            colorType={
              errors[NEWSLETTER_KEY.EMAIL] &&
              touched[NEWSLETTER_KEY.EMAIL]
                ? Input.COLOR_TYPE.ERROR
                : null
            }
          />
        </FormGroup>

        <FormButtons
          justifyContent={FormButtons.JUSTIFY.START}
        >
          <ButtonLoader
            w='100%'
            type='submit'
            isLoading={isSubmitting}
            disabled={isSubmitting}
            onClick={
              (!isValid || !dirty)
                ? null
                : handleClick
            }
          >
            {formatMessageIntlUtil(intl, messages.join)}
          </ButtonLoader>
        </FormButtons>

        <div style={{ display: 'none' }}>
          <Input
            name={NEWSLETTER_KEY.BOT_CHECKER}
            onChange={handleChange}
            id={NEWSLETTER_KEY.BOT_CHECKER}
            value={values[NEWSLETTER_KEY.BOT_CHECKER]}
          />
          {
            errors[NEWSLETTER_KEY.BOT_CHECKER] &&
            touched[NEWSLETTER_KEY.BOT_CHECKER] && (
              <div>{errors[NEWSLETTER_KEY.BOT_CHECKER]}</div>
            )
          }
        </div>
      </Form>
    )
  }

  return (
    <ContentBoxBackgroundContent
      p={3}
      style={{
        maxWidth: '420px'
      }}
      {...rest}
    >
      <View
        style={{
          maxWidth: '600px'
        }}
      >
        {
          submitStatus === NEWSLETTER_SUBMIT_STATUS.SUCCESS && (
            <NewsletterFormSuccess />
          )
        }

        {
          submitStatus === NEWSLETTER_SUBMIT_STATUS.FAILURE && (
            <NewsletterFormFailure />
          )
        }

        {
          submitStatus == null && (
            <Formik
              initialValues={{
                [NEWSLETTER_KEY.FIRST_NAME]: '',
                [NEWSLETTER_KEY.EMAIL]: '',
                [NEWSLETTER_KEY.BOT_CHECKER]: ''
              }}
              validationSchema={validationSchema}
              onSubmit={(values, formik) => {
                handleFormSubmitNewsletterUtil({
                  values,
                  formik,
                  setSubmitStatus
                })
              }}
            >
              {renderFormContent}
            </Formik>
          )
        }
      </View>
    </ContentBoxBackgroundContent>
  )
}
